import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, PencilSimple, Trash, Crown, Shield, User as UserIcon, X as XIcon } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const ROLE_META = {
  admin: { label: "Admin", icon: Crown, color: "bg-[hsl(var(--text-strong))] text-[hsl(var(--background))]" },
  yonetici: { label: "Yönetici", icon: Shield, color: "bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] border border-[hsl(var(--accent-gold))]/30" },
  personel: { label: "Personel", icon: UserIcon, color: "bg-[hsl(var(--secondary))] text-[hsl(var(--text-strong))] border border-[hsl(var(--border))]" },
};

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "personel",
    assigned_customer_ids: [],
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const isAdmin = currentUser?.role === "admin";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [u, c] = await Promise.all([
        axios.get(`${API_URL}/api/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/customers`, { withCredentials: true }),
      ]);
      setUsers(u.data);
      setCustomers(c.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) {
      toast.error("Sadece admin değişiklik yapabilir");
      return;
    }
    try {
      if (editing) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        await axios.put(`${API_URL}/api/users/${editing.id}`, payload, { withCredentials: true });
        toast.success("Kullanıcı güncellendi");
      } else {
        await axios.post(`${API_URL}/api/users`, formData, { withCredentials: true });
        toast.success("Kullanıcı oluşturuldu");
      }
      fetchData();
      handleClose();
    } catch (error) {
      const msg = error.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Kaydedilemedi");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu kullanıcıyı silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API_URL}/api/users/${id}`, { withCredentials: true });
      toast.success("Silindi");
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Silinemedi");
    }
  };

  const handleEdit = (u) => {
    setEditing(u);
    setFormData({
      email: u.email,
      password: "",
      name: u.name,
      role: u.role,
      assigned_customer_ids: u.assigned_customer_ids || [],
    });
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditing(null);
    setFormData({ email: "", password: "", name: "", role: "personel", assigned_customer_ids: [] });
  };

  const toggleCustomer = (cid) => {
    setFormData((p) => ({
      ...p,
      assigned_customer_ids: p.assigned_customer_ids.includes(cid)
        ? p.assigned_customer_ids.filter((x) => x !== cid)
        : [...p.assigned_customer_ids, cid],
    }));
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-[hsl(var(--border))] border-t-[hsl(var(--text-strong))]"></div></div>;
  }

  return (
    <div className="p-8" data-testid="users-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tighter text-[hsl(var(--text-strong))] mb-2">Personel Yönetimi</h1>
          <p className="text-sm text-[hsl(var(--text-soft))] font-light">Ekip üyelerini tanımlayın ve yetkilerini belirleyin</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowModal(true)}
            data-testid="add-user-button"
            className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-medium text-sm shadow-premium hover:opacity-90 premium-hover"
          >
            <Plus size={18} weight="bold" />
            Yeni Kullanıcı
          </button>
        )}
      </div>

      {/* Role explanation */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]">
          <div className="flex items-center gap-2 mb-1"><Crown size={16} weight="duotone" className="text-[hsl(var(--text-strong))]" /><p className="text-sm font-semibold text-[hsl(var(--text-strong))]">Admin</p></div>
          <p className="text-xs text-[hsl(var(--text-soft))]">Tüm yetkiler + kullanıcı yönetimi</p>
        </div>
        <div className="p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]">
          <div className="flex items-center gap-2 mb-1"><Shield size={16} weight="duotone" className="text-[hsl(var(--accent-gold))]" /><p className="text-sm font-semibold text-[hsl(var(--text-strong))]">Yönetici</p></div>
          <p className="text-xs text-[hsl(var(--text-soft))]">Tüm müşteri ve işlem yönetimi (kullanıcı eklemez)</p>
        </div>
        <div className="p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]">
          <div className="flex items-center gap-2 mb-1"><UserIcon size={16} weight="duotone" className="text-[hsl(var(--text-soft))]" /><p className="text-sm font-semibold text-[hsl(var(--text-strong))]">Personel</p></div>
          <p className="text-xs text-[hsl(var(--text-soft))]">Yalnızca atanan müşterilerin verilerini görür</p>
        </div>
      </div>

      {/* Users Table */}
      <div className="rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] overflow-hidden shadow-premium-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
                <th className="text-left px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Ad</th>
                <th className="text-left px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">E-posta</th>
                <th className="text-center px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Rol</th>
                <th className="text-center px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Atanan Müşteri</th>
                <th className="text-right px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, idx) => {
                const meta = ROLE_META[u.role] || ROLE_META.personel;
                const RoleIcon = meta.icon;
                return (
                  <tr key={u.id} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]/40" data-testid={`user-row-${idx}`}>
                    <td className="px-5 py-3.5 text-sm font-medium text-[hsl(var(--text-strong))]">{u.name}</td>
                    <td className="px-5 py-3.5 text-sm text-[hsl(var(--text-soft))]">{u.email}</td>
                    <td className="px-5 py-3.5 text-center">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider ${meta.color}`}>
                        <RoleIcon size={12} weight="bold" />
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-center text-sm text-[hsl(var(--text-soft))] tabular-nums">
                      {u.role === "personel" ? `${u.assigned_customer_ids?.length || 0} müşteri` : "Tümü"}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex justify-end gap-1">
                        {isAdmin && (
                          <>
                            <button onClick={() => handleEdit(u)} data-testid={`edit-user-${idx}`} className="p-2 rounded-lg hover:bg-[hsl(var(--secondary))] text-[hsl(var(--text-soft))] hover:text-[hsl(var(--text-strong))]">
                              <PencilSimple size={14} weight="bold" />
                            </button>
                            {u.id !== currentUser?.id && (
                              <button onClick={() => handleDelete(u.id)} data-testid={`delete-user-${idx}`} className="p-2 rounded-lg hover:bg-[hsl(var(--danger))]/10 text-[hsl(var(--text-soft))] hover:text-[hsl(var(--danger))]">
                                <Trash size={14} weight="bold" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="rounded-2xl border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="user-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold tracking-tight text-[hsl(var(--text-strong))]">
              {editing ? "Kullanıcıyı Düzenle" : "Yeni Kullanıcı"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="user-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Ad *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="user-name-input" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">E-posta *</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="user-email-input" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]" />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">
                Şifre {editing ? "(boş bırak = değiştirme)" : "*"}
              </label>
              <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required={!editing} data-testid="user-password-input" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]" />
            </div>

            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Rol *</label>
              <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} data-testid="user-role-select" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]">
                <option value="personel">Personel — Yalnızca atanan müşteriler</option>
                <option value="yonetici">Yönetici — Tüm veriler (kullanıcı yönetimi hariç)</option>
                <option value="admin">Admin — Tüm yetkiler</option>
              </select>
            </div>

            {formData.role === "personel" && (
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">
                  Atanan Müşteriler ({formData.assigned_customer_ids.length} seçili)
                </label>
                <div className="max-h-60 overflow-y-auto rounded-lg border border-[hsl(var(--border))] p-2 bg-[hsl(var(--background))]" data-testid="customer-assign-list">
                  {customers.length === 0 ? (
                    <p className="text-xs text-[hsl(var(--text-faint))] p-2">Müşteri bulunamadı</p>
                  ) : customers.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[hsl(var(--secondary))] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.assigned_customer_ids.includes(c.id)}
                        onChange={() => toggleCustomer(c.id)}
                        data-testid={`assign-customer-${c.id}`}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-[hsl(var(--text-strong))]">{c.name}</span>
                      {c.sector && <span className="text-[10px] text-[hsl(var(--text-faint))] ml-auto uppercase">{c.sector}</span>}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-3">
              <button type="button" onClick={handleClose} data-testid="cancel-user-button" className="flex-1 px-4 py-2.5 rounded-lg border border-[hsl(var(--border))] text-sm font-medium text-[hsl(var(--text-soft))] hover:bg-[hsl(var(--secondary))]">İptal</button>
              <button type="submit" data-testid="save-user-button" className="flex-1 px-4 py-2.5 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold hover:opacity-90 shadow-premium-sm">Kaydet</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
